import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { translateAuthError } from "./AuthErrorTranslations";
import { trackAuthEvent, logError } from "@/utils/sentry";
import TurnstileWidget from "./TurnstileWidget";

interface LoginFormProps {
  onResetPassword: () => void;
}

const LoginForm = ({ onResetPassword }: LoginFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const { toast } = useToast();

  const handleTurnstileVerify = useCallback((token: string) => {
    setCaptchaToken(token);
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        trackAuthEvent('login_failure', { errorMessage: error.message });
        toast({
          title: "Błąd logowania",
          description: translateAuthError(error.message),
          variant: "destructive",
        });
      } else {
        trackAuthEvent('login_success');
      }
    } catch (error) {
      logError('Login unexpected error', error, { flow: 'login' });
      toast({
        title: "Błąd",
        description: "Wystąpił nieoczekiwany błąd",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleLogin} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="login-email">Email</Label>
        <Input
          id="login-email"
          type="email"
          placeholder="twoj@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="h-11"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="login-password">Hasło</Label>
        <div className="relative">
          <Input
            id="login-password"
            type={showPassword ? "text" : "password"}
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="h-11 pr-10"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="link"
          className="p-0 h-auto text-sm text-muted-foreground hover:text-primary"
          onClick={onResetPassword}
        >
          Zapomniałeś hasła?
        </Button>
      </div>

      <TurnstileWidget onVerify={handleTurnstileVerify} />

      <Button
        type="submit"
        className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl font-medium"
        disabled={isLoading || !captchaToken}
      >
        {isLoading ? "Logowanie..." : "Zaloguj się"}
      </Button>
    </form>
  );
};

export default LoginForm;
